// src/components/pages/admin-dash/AdminMatchResultExtraBallImpact.jsx
import React, { useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import AdminMatchResultFilterBar from "./AdminMatchResultFilterBar";
import AdminStatCard from "./AdminStatCard";

/**
 * Admin report — how much having the extra ball (Powerball/Megaball)
 * also match matters (GET /admin/match-result/extra-ball-impact).
 * Roadmap item 3.5. A binary split (matched vs. not) reads faster as
 * two stat tiles than as a two-bar chart, per the dataviz skill's form
 * heuristic (a single headline comparison, not a distribution).
 */
const fmtUsd = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n)
    : "$0";

export default function AdminMatchResultExtraBallImpact() {
  const [typeLottery, setTypeLottery] = useState("");
  const [parameter, setParameter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchImpact = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (typeLottery) params.typeLottery = typeLottery;
      if (parameter) params.parameter = parameter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await axiosInstance.get("/admin/match-result/extra-ball-impact", { params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError("Failed to load extra-ball impact.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setTypeLottery(""); setParameter(""); setStartDate(""); setEndDate("");
    setRows(null); setError("");
  };

  const matched = rows?.find((r) => r.extraMatched === true);
  const notMatched = rows?.find((r) => r.extraMatched === false);
  const totalLines = (matched?.totalLines || 0) + (notMatched?.totalLines || 0);
  const matchedPct = totalLines > 0 ? Math.round(((matched?.totalLines || 0) / totalLines) * 100) : null;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Extra ball impact
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          How often the Powerball/Mega Ball also matches, and how much prize that
          accounts for.
        </p>
      </header>

      <AdminMatchResultFilterBar
        typeLottery={typeLottery} setTypeLottery={setTypeLottery}
        parameter={parameter} setParameter={setParameter}
        startDate={startDate} setStartDate={setStartDate}
        endDate={endDate} setEndDate={setEndDate}
        onSearch={fetchImpact}
        onReset={handleReset}
        loading={loading}
      />

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}
        >
          {error}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <AdminStatCard
          label="Extra ball matched"
          value={matched ? matched.totalLines.toLocaleString() : "—"}
          helper={matched ? `${fmtUsd(matched.totalPrizeUsd)} in prizes${matchedPct != null ? ` · ${matchedPct}% of lines` : ""}` : "Search to load"}
          icon="star"
          tone="gold"
          loading={loading}
        />
        <AdminStatCard
          label="Extra ball not matched"
          value={notMatched ? notMatched.totalLines.toLocaleString() : "—"}
          helper={notMatched ? `${fmtUsd(notMatched.totalPrizeUsd)} in prizes${matchedPct != null ? ` · ${100 - matchedPct}% of lines` : ""}` : "Search to load"}
          icon="x"
          loading={loading}
        />
      </div>
    </div>
  );
}
